import { useEffect } from "react";
import { useGlobalStore } from "./global";
import { getObtenerValorPorCodigo } from "../services/parameters";

/**
 * Gate de la pantalla de Beneficios: define qué beneficios muestran el botón
 * "Aplicar" (inician una solicitud). Los que no estén en la lista muestran
 * "Ver Información" (solo lectura).
 *
 * A diferencia de los feature flags booleanos (`featureFlags.ts`), este parámetro
 * es una lista de IDs separados por coma (ej. "13,5"). Por eso se resuelve aquí
 * con su propia lógica en lugar de en el sistema de flags booleanos, pero comparte
 * el mismo patrón: override (localStorage) > Parametro del servidor > env > default.
 */
export const BENEFICIOS_APLICAR_OFV = {
  /** Codigo en TBL_PARAMETROS (SQL Server) leído vía ObtenerValorPorCodigo. */
  serverParamCode: "BENEFICIOS_APLICAR_OFV",
  /** Default de build-time (REACT_APP_FF_...). */
  envVar: "REACT_APP_FF_BENEFICIOS_APLICAR_OFV",
  /** Override por navegador (para pruebas, gestionado en /devtools). */
  localStorageKey: "ff:BENEFICIOS_APLICAR_OFV",
  label: "Beneficios con botón Aplicar",
  description:
    'Lista de COD_BENEFICIO (separados por coma) que muestran "Aplicar". El resto muestra "Ver Información".',
  /** Fallback seguro: solo Cesantía por Edad Avanzada (COD_BENEFICIO = 13). */
  default: "13",
} as const;

/** Convierte una lista separada por coma en un Set de IDs, ignorando vacíos/no numéricos. */
export function parseBenefitIds(value: unknown): Set<number> {
  const ids = new Set<number>();
  if (value === undefined || value === null) {
    return ids;
  }
  String(value)
    .split(",")
    .forEach((part) => {
      const id = Number(part.trim());
      if (Number.isInteger(id) && id > 0) {
        ids.add(id);
      }
    });
  return ids;
}

export function getBeneficiosAplicarLocalOverride(): string | undefined {
  try {
    const value = localStorage.getItem(BENEFICIOS_APLICAR_OFV.localStorageKey);
    return value === null ? undefined : value;
  } catch {
    return undefined;
  }
}

export function setBeneficiosAplicarLocalOverride(value: string | undefined): void {
  try {
    if (value === undefined) {
      localStorage.removeItem(BENEFICIOS_APLICAR_OFV.localStorageKey);
    } else {
      localStorage.setItem(BENEFICIOS_APLICAR_OFV.localStorageKey, value);
    }
  } catch {
    /* localStorage no disponible (modo privado, etc.) - ignorar */
  }
}

function getEnvDefault(): string | undefined {
  const value = process.env[BENEFICIOS_APLICAR_OFV.envVar];
  return value === undefined || value === "" ? undefined : value;
}

export type BeneficiosAplicarSource = "override" | "server" | "env" | "default";

export interface ResolvedBeneficiosAplicar {
  value: string;
  source: BeneficiosAplicarSource;
}

/**
 * Resuelve el valor crudo (string CSV) con la prioridad:
 * override (localStorage) > Parametro del servidor > env > default.
 */
export function resolveBeneficiosAplicar(opts: {
  serverValue?: string;
  override?: string;
}): ResolvedBeneficiosAplicar {
  if (opts.override !== undefined) {
    return { value: opts.override, source: "override" };
  }
  if (opts.serverValue !== undefined) {
    return { value: opts.serverValue, source: "server" };
  }
  const env = getEnvDefault();
  if (env !== undefined) {
    return { value: env, source: "env" };
  }
  return { value: BENEFICIOS_APLICAR_OFV.default, source: "default" };
}

/** Resuelve el valor + fuente desde el store (reactivo). */
export function useResolvedBeneficiosAplicar(): ResolvedBeneficiosAplicar {
  const serverValue = useGlobalStore((s) => s.beneficiosAplicarServer);
  const override = useGlobalStore((s) => s.beneficiosAplicarOverride);
  return resolveBeneficiosAplicar({ serverValue, override });
}

/** Set de IDs de beneficios que pueden mostrar el botón "Aplicar". */
export function useBeneficiosAplicarIds(): Set<number> {
  const { value } = useResolvedBeneficiosAplicar();
  return parseBenefitIds(value);
}

/**
 * Lee el Parametro del backend una vez al iniciar la app y lo empuja al store.
 * Un Parametro ausente/fallido/inactivo se ignora, dejando el flag en su env/default.
 */
export function useInitBeneficiosAplicar(): void {
  const setBeneficiosAplicarServer = useGlobalStore(
    (s) => s.setBeneficiosAplicarServer
  );

  useEffect(() => {
    let cancelled = false;

    getObtenerValorPorCodigo(BENEFICIOS_APLICAR_OFV.serverParamCode)
      .then((resp) => {
        const data = resp?.data;
        if (cancelled || !data?.successed || data.value?.estado !== true) {
          return;
        }
        const valor = data.value?.valor;
        if (valor !== undefined && valor !== null) {
          setBeneficiosAplicarServer(String(valor));
        }
      })
      .catch(() => {
        /* dejar que caiga al env/default */
      });

    return () => {
      cancelled = true;
    };
  }, [setBeneficiosAplicarServer]);
}
